import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { setAuthToken, getAuthToken } from '@/lib/api';

const AuthContext = createContext(null);

const ALL_ROLES = ["Administrator", "Owner", "Office Manager", "Employee"];
const INACTIVITY_TIMEOUT = 6 * 60 * 60 * 1000; // 6 hours in ms

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAuthToken());
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [permissions, setPermissions] = useState(null);
  // previewPerms: when set, hasPerm() returns values from this object instead
  // of the user's real permissions. Used by admins to sanity-check a proposed
  // OM permission set without saving it first.
  const [previewPerms, setPreviewPerms] = useState(null);
  const inactivityTimer = useRef(null);

  const logout = useCallback(() => {
    setAuthToken(null);
    localStorage.removeItem('nycd-theme');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('nycd-tasks-filters');
    // Reset force-refresh baseline so the next user on this tab starts fresh.
    try { sessionStorage.removeItem('forceRefreshBaselineAt'); } catch { /* ignore */ }
    setToken(null);
    setUser(null);
    setViewAsRole(null);
    setRequires2FASetup(false);
    setPermissions(null);
    setPreviewPerms(null);
    document.documentElement.setAttribute('data-theme', 'dark');
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
  }, []);

  // Inactivity logout
  const resetInactivityTimer = useCallback(() => {
    if (!token) return;
    localStorage.setItem('lastActivity', Date.now().toString());
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      logout();
      window.location.reload();
    }, INACTIVITY_TIMEOUT);
  }, [token, logout]);

  
  useEffect(() => {
    if (!token) return;
    const last = parseInt(localStorage.getItem('lastActivity') || '0');
    if (last && Date.now() - last > INACTIVITY_TIMEOUT) {
      logout();
      return;
    }
    resetInactivityTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer));
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [token, resetInactivityTimer, logout]);
  

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      if (res.data.totp_enabled) {
        setRequires2FASetup(false);
      }
      if (res.data.theme_preference) {
        const stored = localStorage.getItem('nycd-theme');
        if (!stored) {
          localStorage.setItem('nycd-theme', res.data.theme_preference);
          document.documentElement.setAttribute('data-theme', res.data.theme_preference);
        }
      }
      // Fetch effective permissions (role defaults merged with OM overrides).
      try {
        const pr = await api.get('/auth/me/permissions');
        setPermissions(pr.data || {});
      } catch {
        setPermissions({});
      }
    } catch {
      setAuthToken(null);
      setToken(null);
      setUser(null);
      setPermissions(null);
    }
    setLoading(false);
  }, [token]);

  
  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const res = await api.post('/auth/login', payload);
    if (res.data.requires_2fa) {
      return { requires_2fa: true };
    }
    const { token: t, user: u, requires_2fa_setup } = res.data;
    setAuthToken(t);
    localStorage.setItem('lastActivity', Date.now().toString());
    setToken(t);
    setUser(u);
    setViewAsRole(null);
    // Pull effective permissions for the new session.
    try {
      const pr = await api.get('/auth/me/permissions');
      setPermissions(pr.data || {});
    } catch {
      setPermissions({});
    }
    if (requires_2fa_setup) {
      setRequires2FASetup(true);
      return { requires_2fa_setup: true };
    }
    if (u.theme_preference) {
      localStorage.setItem('nycd-theme', u.theme_preference);
      document.documentElement.setAttribute('data-theme', u.theme_preference);
    }
    return u;
  };

  const complete2FASetup = () => {
    setRequires2FASetup(false);
  };

  const isRealAdmin = user?.role === 'Administrator';
  const effectiveRole = (isRealAdmin && viewAsRole) ? viewAsRole : user?.role;

  // Check granular permission. Returns true if the user has the action perm.
  // Visibility keys ('projects_visibility' etc) return their mode string.
  // When previewPerms is active, use it instead (admin "preview as OM" mode).
  const hasPerm = (key) => {
    if (previewPerms) return !!previewPerms[key];
    if (!permissions) return false;
    return !!permissions[key];
  };
  const getVisibility = (key) => (previewPerms || permissions || {})[key] || 'assigned';

  const startPermPreview = (perms) => setPreviewPerms(perms || {});
  const stopPermPreview = () => setPreviewPerms(null);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, setUser,
      effectiveRole, viewAsRole, setViewAsRole, isRealAdmin, ALL_ROLES,
      requires2FASetup, complete2FASetup,
      permissions, hasPerm, getVisibility,
      previewPerms, startPermPreview, stopPermPreview,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
